import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  MessageSquareQuote, 
  UserCog,
  Menu,
  X,
  LogOut,
  ChevronDown,
  ChevronRight,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Monitor,
  Server,
  FileText,
  FileCheck,
  Mail,
  Ticket,
  ClipboardList,
  Share2,
  Send,
  Code2,
  Settings,
  Briefcase,
  MessageCircle,
  Search,
  DollarSign,
  Activity,
  StickyNote,
  MapPin,
  GitBranch,
  Clock,
  ListChecks,
  HelpCircle,
  FolderOpen,
  Bell,
  Database,
  RefreshCw,
  Network,
  BookOpen,
  BookMarked,
  Package,
  Scale,
  CalendarClock,
  BarChart3,
  Copy,
  ArrowRightLeft,
  Receipt,
  Tag,
  Wrench,
  ClipboardCheck,
  AlertTriangle,
  Headphones,
  Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LOGO_ICON = "/whitewick-logo.png";

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['operations', 'content', 'users']);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const adminUser = localStorage.getItem('adminUser');
    
    if (!token || !adminUser) {
      navigate('/admin/login');
      return;
    }
    
    setAdmin(JSON.parse(adminUser));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Categorized navigation items
  const navCategories = [
    {
      id: 'main',
      label: null,
      items: [
        { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' }
      ]
    },
    {
      id: 'operations',
      label: 'Operations',
      icon: Briefcase,
      items: [
        { path: '/admin/tickets', icon: Ticket, label: 'Tickets' },
        { path: '/admin/interventions', icon: MapPin, label: 'Interventions' },
        { path: '/admin/remote-sessions', icon: Headphones, label: 'Remote Sessions' },
        { path: '/admin/changes', icon: GitBranch, label: 'Changes' },
        { path: '/admin/network', icon: Network, label: 'Network' },
        { path: '/admin/time-tracking', icon: Clock, label: 'Time Tracking' },
        { path: '/admin/onboarding', icon: ListChecks, label: 'Onboarding' },
        { path: '/admin/sla-tracking', icon: Shield, label: 'SLA Tracking' },
        { path: '/admin/scheduled-tickets', icon: CalendarClock, label: 'Scheduled Tickets' },
        { path: '/admin/service-catalog', icon: BookOpen, label: 'Service Catalog' },
        { path: '/admin/assessments', icon: ClipboardCheck, label: 'Assessments' },
        { path: '/admin/postmortems', icon: AlertTriangle, label: 'Post-Mortems' },
        { path: '/admin/client-hours', icon: BarChart3, label: 'Client Hours' }
      ]
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: FileText,
      items: [
        { path: '/admin/invoices', icon: FileText, label: 'Invoices' },
        { path: '/admin/proposals', icon: FileCheck, label: 'Proposals' }
      ]
    },
    {
      id: 'contracts',
      label: 'Contracts',
      icon: Scale,
      items: [
        { path: '/admin/contract-dashboard', icon: LayoutDashboard, label: 'Contract Dashboard' },
        { path: '/admin/contracts', icon: FileText, label: 'All Contracts' },
        { path: '/admin/clauses', icon: BookOpen, label: 'Clause Library' },
        { path: '/admin/company-identity', icon: Building2, label: 'Company Identity' }
      ]
    },
    {
      id: 'quotes',
      label: 'Quotes & Sales',
      icon: DollarSign,
      items: [
        { path: '/admin/quotes', icon: FileText, label: 'All Quotes' },
        { path: '/admin/vendor-quotes', icon: FileText, label: 'Vendor Quotes' },
        { path: '/admin/quote-workspaces', icon: FolderOpen, label: 'Workspaces' },
        { path: '/admin/packages', icon: Package, label: 'Packages' },
        { path: '/admin/quote-templates', icon: Copy, label: 'Templates' },
        { path: '/admin/quote-settings', icon: Settings, label: 'Quote Settings' },
      ]
    },
    {
      id: 'communications',
      label: 'Messages',
      icon: MessageCircle,
      items: [
        { path: '/admin/messages', icon: Mail, label: 'Contact Forms' },
        { path: '/admin/client-messages', icon: Send, label: 'Client Messages' }
      ]
    },
    {
      id: 'users',
      label: 'Clients',
      icon: Users,
      items: [
        { path: '/admin/customers', icon: Users, label: 'Users' },
        { path: '/admin/companies', icon: Building2, label: 'Companies' },
        { path: '/admin/client-files', icon: FolderOpen, label: 'Files' },
        { path: '/admin/knowledge-base', icon: BookMarked, label: 'Knowledge Base' },
        { path: '/admin/ipam', icon: Network, label: 'IPAM' },
        { path: '/admin/assets', icon: Package, label: 'Assets' },
        { path: '/admin/racks', icon: Server, label: 'Rack Management' },
        { path: '/admin/power-graph', icon: Zap, label: 'Power Graph' },
        { path: '/admin/maintenance-notices', icon: Wrench, label: 'Maintenance Notices' },
        { path: '/admin/notification-prefs', icon: Bell, label: 'Notifications' }
      ]
    },
    {
      id: 'content',
      label: 'Website',
      icon: Monitor,
      items: [
        { path: '/admin/team', icon: UserCog, label: 'Team' },
        { path: '/admin/testimonials', icon: MessageSquareQuote, label: 'Testimonials' },
        { path: '/admin/technologies', icon: Monitor, label: 'Technologies' },
        { path: '/admin/social-links', icon: Share2, label: 'Social Links' },
        { path: '/admin/faq', icon: HelpCircle, label: 'FAQ' },
        { path: '/admin/seo', icon: Search, label: 'SEO' }
      ]
    },
    {
      id: 'finance',
      label: 'Finance',
      icon: Receipt,
      items: [
        { path: '/admin/expenses', icon: Receipt, label: 'Expenses' },
        { path: '/admin/expense-categories', icon: Tag, label: 'Expense Categories' },
      ]
    },
    {
      id: 'system',
      label: 'Settings',
      icon: Settings,
      items: [
        { path: '/admin/api-settings', icon: Settings, label: 'Integrations' },
        { path: '/admin/integration-sync', icon: RefreshCw, label: 'Integration Sync' },
        { path: '/admin/dolibarr-sync', icon: ArrowRightLeft, label: 'Dolibarr Sync' },
        { path: '/admin/glpi-sync', icon: ArrowRightLeft, label: 'GLPI Sync' },
        { path: '/admin/sync-log', icon: Activity, label: 'Sync Activity Log' },
        { path: '/admin/push-notifications', icon: Bell, label: 'Push Notifications' },
        { path: '/admin/notification-settings', icon: Bell, label: 'My Notifications' },
        { path: '/admin/email-templates', icon: Mail, label: 'Email Templates' },
        { path: '/admin/email-providers', icon: Mail, label: 'Email Providers' },
        { path: '/admin/notification-log', icon: Bell, label: 'Notification Log' },
        { path: '/admin/canned-responses', icon: StickyNote, label: 'Canned Responses' },
        { path: '/admin/admins', icon: Shield, label: 'Admin Users' },
        { path: '/admin/admin-roles', icon: Shield, label: 'Admin Roles' },
        { path: '/admin/client-roles', icon: ShieldCheck, label: 'Client Access Roles' },
        { path: '/admin/careers', icon: Briefcase, label: 'Careers & Recruiting' },
        { path: '/admin/security', icon: ShieldAlert, label: 'Security Dashboard' },
        { path: '/admin/activity-log', icon: Activity, label: 'Activity Log' },
        { path: '/admin/login-audit', icon: ClipboardList, label: 'Login Audit' },
        { path: '/admin/documentation', icon: BookOpen, label: 'Documentation' },
        ...(admin?.role === 'super_admin' ? [
          { path: '/admin/performance', icon: Database, label: 'Performance', superAdminOnly: true },
          { path: '/admin/api-docs', icon: Code2, label: 'API Docs', superAdminOnly: true }
        ] : [])
      ]
    }
  ];

  const isActive = (path) => location.pathname === path;

  // Render nav items for a category
  const renderNavItems = (items, isMobile = false) => (
    items.map((item) => (
      <Link
        key={item.path}
        to={item.path}
        onClick={isMobile ? () => setSidebarOpen(false) : undefined}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
          isActive(item.path)
            ? 'bg-blue-50 text-[#1e3a5f] border border-blue-100'
            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
        }`}
      >
        <item.icon className="h-4 w-4" />
        <span className="font-medium text-sm">{item.label}</span>
      </Link>
    ))
  );

  // Render category with collapsible header
  const renderCategory = (category, isMobile = false) => {
    if (!category.label) {
      // Main items without category header
      return (
        <div key={category.id} className="space-y-1">
          {renderNavItems(category.items, isMobile)}
        </div>
      );
    }

    const isExpanded = expandedCategories.includes(category.id);
    const CategoryIcon = category.icon;

    return (
      <div key={category.id} className="space-y-1">
        <button
          onClick={() => toggleCategory(category.id)}
          className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-600 transition-colors"
        >
          <div className="flex items-center gap-2">
            <CategoryIcon className="h-3.5 w-3.5" />
            {category.label}
          </div>
          <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        </button>
        {isExpanded && (
          <div className="space-y-1 ml-2">
            {renderNavItems(category.items, isMobile)}
          </div>
        )}
      </div>
    );
  };

  if (!admin) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a5f]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-72 bg-white border-r border-slate-200 fixed h-full">
        <div className="h-28 flex items-center px-4 border-b border-slate-200">
          <img src={LOGO_ICON} alt="Whitewick IT" className="h-24 w-auto" />
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-4 overflow-y-auto">
          {navCategories.map((category) => renderCategory(category, false))}
        </nav>
        
        <div className="p-4 border-t border-slate-200">
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white transform transition-transform duration-200 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="h-16 flex items-center justify-between px-6 border-b border-slate-200">
          <img src={LOGO_ICON} alt="Whitewick IT" className="h-20 w-auto" />
          <button onClick={() => setSidebarOpen(false)} className="text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-4 overflow-y-auto max-h-[calc(100vh-4rem)]">
          {navCategories.map((category) => renderCategory(category, true))}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-72">
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          <button 
            className="lg:hidden text-slate-600"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          
          <div className="flex-1" />
          
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-slate-700">
                  <div className="w-8 h-8 rounded-full bg-[#1e3a5f] flex items-center justify-center text-white font-medium">
                    {admin?.full_name?.charAt(0) || 'A'}
                  </div>
                  <span className="hidden sm:inline">{admin?.full_name}</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white border border-slate-200">
                <DropdownMenuItem className="text-slate-600">
                  <span className="text-xs">{admin?.email}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
